// Single source of truth for brand-colour maths used by the white-label login
// gate (Auth.tsx) and its live preview (BrandingPreview.tsx). Keeping it here
// stops the two surfaces drifting apart.

export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** WCAG relative luminance (linearised sRGB) of a #rrggbb colour. */
export function relLuminance(hex: string): number {
  const ch = [0, 2, 4]
    .map((i) => parseInt(hex.slice(1 + i, 3 + i), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** Rough "is this a light colour" test (for picking a dark vs light surface). */
export function isLightHex(hex: string): boolean {
  return HEX_RE.test(hex) && relLuminance(hex) > 0.4;
}

/**
 * Returns white or near-black text — whichever has the HIGHER WCAG contrast on
 * `bg`. Fixes the "white text on a light brand colour" failure. A non-hex value
 * (e.g. a CSS variable fallback) is assumed dark, so white text is returned.
 */
export function readableTextOn(bg: string): string {
  if (!HEX_RE.test(bg)) return '#ffffff';
  const L = relLuminance(bg);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;
  return contrastWhite >= contrastBlack ? '#ffffff' : '#1a1a1a';
}

/** Aurora gradient woven from the brand colours for the glassmorphism style. */
export function auroraGradient(primary: string, secondary: string, base: string): string {
  return `radial-gradient(circle at 18% 20%, ${primary}59, transparent 45%), radial-gradient(circle at 82% 75%, ${secondary}59, transparent 45%), linear-gradient(135deg, ${base}, #14101f)`;
}

/** "H S% L%" triplet of a #rrggbb colour, the form the theme variables use. */
export function hexToHslTriplet(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** `a` blended over `b`, `weightB` of the way towards `b` (0..1). */
export function mixHex(a: string, b: string, weightB: number): string {
  const ch = (hex: string, i: number) => parseInt(hex.slice(i, i + 2), 16);
  return '#' + [1, 3, 5]
    .map((i) => Math.round(ch(a, i) * (1 - weightB) + ch(b, i) * weightB).toString(16).padStart(2, '0'))
    .join('');
}
